import { useRef, useContext } from 'react'
import { motion } from 'framer-motion'
import Footer from '../landing/components/Footer'
import { useInView } from '../hooks/useInView'
import { RouteTransitionContext } from '../components/AnimatedRoutes'

/* ── UI animation variants ──────────────────────── */
const staggerUI = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.07, delayChildren: 0.2 } },
}

const fadeUp = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease: [0.38, 0.005, 0.215, 1] as const } },
}

/* ── Hero animation variants ────────────────────── */
const heroStagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } },
}

const heroFadeUp = {
  hidden: { opacity: 0, y: 60 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: [0.38, 0.005, 0.215, 1] as const },
  },
}

/* ── Integration data for alternating layout ────── */
const integrations = [
  {
    title: 'Webhooks, bots & sharable links',
    desc: 'Push your community out to the platforms it already lives on — Discord, Facebook, and beyond.',
    bullets: [
      { label: 'Webhooks', text: 'Fire real-time events into any endpoint the moment something happens in your community.' },
      { label: 'Platform bots', text: 'Native Discord and Facebook bots surface drops, listings, and updates where members hang out.' },
      { label: 'Sharable links', text: 'Every product and group gets a link that unfurls beautifully across social platforms.' },
    ],
    ui: (
      <>
        <motion.div variants={fadeUp} className="mb-1 flex items-center justify-between">
          <span className="text-[13px] font-semibold text-oak-black">Connected channels</span>
          <span className="text-[11px] text-oak-green">3 active</span>
        </motion.div>
        {[
          { name: 'Discord · #new-drops', meta: 'Bot posts new releases automatically', status: 'Live' },
          { name: 'Facebook Group', meta: 'Sharable links + product cards', status: 'Live' },
          { name: 'Webhook · order.created', meta: 'POST to your endpoint', status: 'Live' },
        ].map((c, i) => (
          <motion.div key={i} variants={fadeUp} className="flex items-center justify-between rounded-xl border border-oak-black/[0.06] bg-oak-black/[0.02] px-3.5 py-3">
            <div>
              <div className="text-[13px] font-medium text-oak-black">{c.name}</div>
              <div className="text-[11px] text-oak-black/40">{c.meta}</div>
            </div>
            <span className="rounded-full bg-oak-green/10 px-2.5 py-0.5 text-[11px] font-medium text-oak-green">{c.status}</span>
          </motion.div>
        ))}
      </>
    ),
  },
  {
    title: 'Connected straight to the source',
    desc: 'Open-source projects and platforms like Metafy connect straight to your community. No scraping, no stale copies.',
    bullets: [
      { label: 'Straight from the source', text: 'Partners read from the same place your community does, so everything is always current.' },
      { label: 'Open by design', text: 'Open-source tools and other platforms plug in through a clean, documented API.' },
      { label: 'Nothing to break', text: 'No brittle scrapers. When something changes, every connected platform sees it right away.' },
    ],
    ui: (
      <>
        <motion.div variants={fadeUp} className="flex items-center justify-between">
          <span className="text-[13px] font-semibold text-oak-black">Connections</span>
          <span className="text-[11px] text-oak-green">In sync</span>
        </motion.div>
        <motion.div variants={fadeUp} className="flex items-center justify-between rounded-xl border border-oak-black/[0.06] bg-oak-black/[0.02] px-3.5 py-3">
          <div className="flex items-center gap-2.5">
            <span className="text-[16px]">🎮</span>
            <div>
              <div className="text-[13px] font-medium text-oak-black">Metafy</div>
              <div className="text-[11px] text-oak-black/40">Direct connection · no scraping</div>
            </div>
          </div>
          <span className="text-[11px] text-oak-black/40">updated 1 min ago</span>
        </motion.div>
        <motion.div variants={fadeUp} className="flex items-center justify-between rounded-xl border border-oak-black/[0.06] bg-oak-black/[0.02] px-3.5 py-3">
          <div className="flex items-center gap-2.5">
            <span className="text-[16px]">{'</>'}</span>
            <div>
              <div className="text-[13px] font-medium text-oak-black">Open-source connector</div>
              <div className="text-[11px] text-oak-black/40">Connected to the source</div>
            </div>
          </div>
          <span className="text-[11px] text-oak-green">Connected</span>
        </motion.div>
      </>
    ),
  },
  {
    title: 'Release once, deliver everywhere',
    desc: 'Release a product and Oak gets it — then delivers it down to your other projects automatically.',
    bullets: [
      { label: 'One release', text: 'Publish a product a single time. Oak picks it up the moment it goes live.' },
      { label: 'Automatic fan-out', text: 'Oak delivers it down to every connected project and platform — no manual re-entry.' },
      { label: 'Always in step', text: 'Downstream projects stay current as new drops and updates roll out over time.' },
    ],
    ui: (
      <>
        <motion.div variants={fadeUp} className="flex items-center justify-between rounded-xl bg-oak-black/[0.03] px-3.5 py-3">
          <div>
            <div className="text-[13px] font-semibold text-oak-black">Trail Ridge Daypack</div>
            <div className="text-[11px] text-oak-black/40">Released · picked up by Oak</div>
          </div>
          <span className="rounded-full bg-oak-green/10 px-2.5 py-0.5 text-[11px] font-medium text-oak-green">New</span>
        </motion.div>
        <motion.div variants={fadeUp} className="pl-3 text-[11px] font-medium uppercase tracking-wider text-oak-black/30">
          Delivered automatically to
        </motion.div>
        {[
          { name: 'Storefront', meta: 'summitco.shop' },
          { name: 'Discord community', meta: '#new-drops' },
          { name: 'Partner project · Metafy', meta: 'via direct connection' },
        ].map((p, i) => (
          <motion.div key={i} variants={fadeUp} className="ml-3 flex items-center justify-between rounded-lg bg-oak-black/[0.03] px-3 py-2">
            <span className="text-[13px] font-medium text-oak-black">{p.name}</span>
            <span className="text-[11px] text-oak-black/40">{p.meta}</span>
          </motion.div>
        ))}
      </>
    ),
  },
]

function AlternatingIntegration({ integration, index }: { integration: (typeof integrations)[number]; index: number }) {
  const ref = useRef<HTMLDivElement>(null)
  const isInView = useInView(ref, { threshold: 0.15 })
  const isTransitioning = useContext(RouteTransitionContext)
  const isReversed = index % 2 === 1
  const show = !isTransitioning && isInView

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 60 }}
      animate={show ? { opacity: 1, y: 0 } : { opacity: 0, y: 60 }}
      transition={{ duration: 0.8, ease: [0.38, 0.005, 0.215, 1] as const }}
      className={`flex flex-col items-center gap-10 md:gap-16 ${isReversed ? 'md:flex-row-reverse' : 'md:flex-row'}`}
    >
      {/* Text side */}
      <div className="flex-1">
        <h3 className="text-[clamp(28px,4vw,40px)] font-medium leading-[1.1] tracking-[-0.8px] text-oak-black">
          {integration.title}
        </h3>
        <p className="mt-3 max-w-[400px] text-[clamp(15px,1.5vw,18px)] leading-[1.5] tracking-[-0.2px] text-oak-black/50">
          {integration.desc}
        </p>
        <div className="mt-6 space-y-4">
          {integration.bullets.map((b, i) => (
            <div key={i}>
              <p className="text-[15px] font-semibold tracking-[-0.2px] text-oak-black">{b.label}</p>
              <p className="mt-0.5 text-[14px] leading-[1.5] text-oak-black/45">{b.text}</p>
            </div>
          ))}
        </div>
      </div>

      {/* UI side */}
      <div className="w-full flex-1">
        <motion.div
          className="space-y-2.5 rounded-2xl border border-oak-black/[0.06] bg-white px-6 py-6 md:px-8"
          style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.04), 0 0 1px rgba(0,0,0,0.06)' }}
          variants={staggerUI}
          initial="hidden"
          animate={show ? 'visible' : 'hidden'}
        >
          {integration.ui}
        </motion.div>
      </div>
    </motion.div>
  )
}

export default function IntegrationsPage() {
  const isTransitioning = useContext(RouteTransitionContext)

  return (
    <div className="min-h-screen bg-oak-cream">
      <section className="pb-8 pt-28 md:pb-16 md:pt-36">
        <motion.div
          variants={heroStagger}
          initial="hidden"
          animate={isTransitioning ? 'hidden' : 'visible'}
          className="mx-auto max-w-nav px-6"
        >
          <motion.span
            variants={heroFadeUp}
            className="text-sm font-semibold uppercase tracking-widest text-gold-500"
          >
            Integrations
          </motion.span>
          <motion.h1
            variants={heroFadeUp}
            className="mt-4 font-serif text-[clamp(48px,8vw,120px)] leading-[0.85] tracking-[-2.88px] text-oak-black"
          >
            Meet them where they are
          </motion.h1>
          <motion.p
            variants={heroFadeUp}
            className="mt-6 max-w-[700px] text-[clamp(16px,2vw,22px)] font-medium leading-[1.3] tracking-[-0.2px] text-oak-black/70 md:mt-8"
          >
            Bots, webhooks, and direct connections that bring your community
            to the places your members already hang out.
          </motion.p>
        </motion.div>
      </section>
      <section className="bg-oak-cream pb-32 pt-8 md:px-16 md:pb-44 md:pt-12">
        <div className="mx-auto max-w-nav px-6 space-y-20 md:space-y-32">
          {integrations.map((integration, i) => (
            <AlternatingIntegration key={integration.title} integration={integration} index={i} />
          ))}
        </div>
      </section>
      <Footer />
    </div>
  )
}
